import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DragIndicator from "@mui/icons-material/DragIndicator";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import Logo from "components/styled/Logo";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";

/**
 * DEV DRAFT ONLY — interactive mockups for ChartMind Exam edit / reorder UX.
 * Route: /chartmind-exam-edit-draft (isDevEnvironment). No session writes.
 *
 * Pick an option number in chat after reviewing the mockups; that choice drives
 * the real ExamPlanStep / Exam2SystemsTable work across Admission, Progress, Events.
 */

const MOCK_SCOPE = "Internal medicine · Inpatient · Admission H&P";

const SEED_SYSTEMS = [
  {
    id: "general",
    name: "GENERAL",
    finding: "WDWN male in NAD",
    hidden: false,
    options: ["WDWN male in NAD", "WDWN female in NAD", "WDWN adult in NAD"],
    preferredNormal: "WDWN male in NAD",
  },
  {
    id: "vitals",
    name: "VITAL SIGNS",
    finding: "WNL",
    hidden: false,
    options: ["WNL", "Tachycardic", "Bradycardic", "Hypertensive", "Hypotensive", "Febrile"],
    preferredNormal: "WNL",
  },
  {
    id: "heent",
    name: "HEENT",
    finding: "",
    hidden: false,
    options: ["Normocephalic atraumatic", "PERRL", "EOMI", "TMs clear", "Oropharynx clear"],
    preferredNormal: "Normocephalic atraumatic",
  },
  {
    id: "heart",
    name: "HEART",
    finding: "RRR no murmur",
    hidden: false,
    options: ["RRR no murmur", "RRR s murmur", "Irregularly irregular", "Tachycardic", "S3 present"],
    preferredNormal: "RRR no murmur",
  },
  {
    id: "lungs",
    name: "LUNGS",
    finding: "CTAB",
    hidden: false,
    options: ["CTAB", "Clear bilaterally", "Bilateral crackles", "Wheezes", "Rhonchi"],
    preferredNormal: "CTAB",
  },
  {
    id: "abdomen",
    name: "ABDOMEN",
    finding: "",
    hidden: false,
    options: ["Soft NT ND", "Soft nontender", "Tender", "Distended", "Guarding"],
    preferredNormal: "Soft NT ND",
  },
  {
    id: "neuro",
    name: "NEURO",
    finding: "A&Ox3, CN II–XII intact",
    hidden: false,
    options: ["A&Ox3, CN II–XII intact", "No focal deficit", "Altered mental status"],
    preferredNormal: "A&Ox3, CN II–XII intact",
  },
  {
    id: "breast",
    name: "BREAST",
    finding: "Deferred",
    hidden: true,
    options: ["No masses or nipple discharge", "Deferred", "Mass palpated"],
    preferredNormal: "Deferred",
  },
];

const OPTIONS = [
  {
    id: 1,
    title: "Option 1 — Inline “Edit layout”",
    description:
      "Stay on the Exam step. Edit layout turns on up/down + rename/hide. Save for this note, or Save as my defaults.",
  },
  {
    id: 2,
    title: "Option 2 — PET-style manager modal",
    description:
      "Exam stays document-first. Customize exam… opens a modal with This note | My defaults tabs — expand a section to edit its finding options.",
  },
  {
    id: 3,
    title: "Option 3 — Document | Configure toggle",
    description:
      "A mode switch at the top of Exam: document findings, or configure systems and defaults.",
  },
  {
    id: 4,
    title: "Option 4 — Side drawer preferences",
    description:
      "Keep documenting; a drawer edits defaults. Apply to this note copies them in without leaving.",
  },
  {
    id: 5,
    title: "Option 5 — My Exam settings page + light override",
    description:
      "Defaults live on a preferences page. Exam only offers Customize for this note (reorder/hide).",
  },
];

const cloneSystems = () =>
  SEED_SYSTEMS.map((row) => ({
    ...row,
    options: [...(row.options || [])],
  }));

const moveRow = (rows, index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= rows.length) return rows;
  const next = [...rows];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

const moveOption = (options, index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= options.length) return options;
  const next = [...options];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

const patchRow = (rows, index, patch) =>
  rows.map((item, i) => (i === index ? { ...item, ...patch } : item));

const ScopeChip = () => (
  <Chip size="small" label={`Defaults scope (mock): ${MOCK_SCOPE}`} color="default" />
);

const FeedbackBanner = ({ message, onClose }) =>
  message ? (
    <Alert severity="success" onClose={onClose} sx={{ mb: 2 }}>
      {message}
    </Alert>
  ) : null;

/** Shared reorder / rename / hide list used by several mockups. */
const LayoutEditorList = ({ rows, onChange, title = "Systems" }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle2" color="text.secondary">
      {title}
    </Typography>
    {rows.map((row, index) => (
      <Paper
        key={row.id}
        variant="outlined"
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          opacity: row.hidden ? 0.55 : 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <IconButton
            size="small"
            aria-label={`Move ${row.name} up`}
            onClick={() => onChange(moveRow(rows, index, -1))}
            disabled={index === 0}
          >
            <ArrowUpward fontSize="small" />
          </IconButton>
          <DragIndicator sx={{ color: "text.secondary", fontSize: 18 }} />
          <IconButton
            size="small"
            aria-label={`Move ${row.name} down`}
            onClick={() => onChange(moveRow(rows, index, 1))}
            disabled={index === rows.length - 1}
          >
            <ArrowDownward fontSize="small" />
          </IconButton>
        </Box>
        <TextField
          size="small"
          label="Section"
          value={row.name}
          onChange={(event) => {
            const next = rows.map((item, i) =>
              i === index ? { ...item, name: event.target.value } : item,
            );
            onChange(next);
          }}
          sx={{ flex: 1, minWidth: 140 }}
        />
        <Tooltip title={row.hidden ? "Show on exam" : "Hide from exam"}>
          <IconButton
            size="small"
            aria-label={row.hidden ? `Show ${row.name}` : `Hide ${row.name}`}
            onClick={() => {
              const next = rows.map((item, i) =>
                i === index ? { ...item, hidden: !item.hidden } : item,
              );
              onChange(next);
            }}
          >
            {row.hidden ? (
              <VisibilityOffOutlined fontSize="small" />
            ) : (
              <VisibilityOutlined fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Paper>
    ))}
  </Stack>
);

/**
 * Option 2 only: section reorder/rename/hide plus expand-to-edit finding options
 * (add, rename, reorder, delete, star preferred normal).
 */
const LayoutEditorListWithOptions = ({ rows, onChange, title = "Systems" }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [draftOption, setDraftOption] = useState("");

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      {rows.map((row, index) => {
        const expanded = expandedId === row.id;
        const options = Array.isArray(row.options) ? row.options : [];
        return (
          <Paper
            key={row.id}
            variant="outlined"
            sx={{
              p: 1.5,
              opacity: row.hidden ? 0.55 : 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <IconButton
                  size="small"
                  aria-label={`Move ${row.name} up`}
                  onClick={() => onChange(moveRow(rows, index, -1))}
                  disabled={index === 0}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <DragIndicator sx={{ color: "text.secondary", fontSize: 18 }} />
                <IconButton
                  size="small"
                  aria-label={`Move ${row.name} down`}
                  onClick={() => onChange(moveRow(rows, index, 1))}
                  disabled={index === rows.length - 1}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                size="small"
                label="Section"
                value={row.name}
                onChange={(event) =>
                  onChange(patchRow(rows, index, { name: event.target.value }))
                }
                sx={{ flex: 1, minWidth: 120 }}
              />
              <Tooltip title={row.hidden ? "Show on exam" : "Hide from exam"}>
                <IconButton
                  size="small"
                  aria-label={row.hidden ? `Show ${row.name}` : `Hide ${row.name}`}
                  onClick={() =>
                    onChange(patchRow(rows, index, { hidden: !row.hidden }))
                  }
                >
                  {row.hidden ? (
                    <VisibilityOffOutlined fontSize="small" />
                  ) : (
                    <VisibilityOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                onClick={() => {
                  setExpandedId(expanded ? null : row.id);
                  setDraftOption("");
                }}
              >
                Options ({options.length})
              </Button>
            </Box>

            {expanded ? (
              <Stack spacing={1} sx={{ mt: 1.5, pl: { xs: 0, sm: 6 } }}>
                <Typography variant="caption" color="text.secondary">
                  Edit findings shown in this section. Star marks your preferred normal for Tab
                  fill.
                </Typography>
                {options.map((option, optionIndex) => (
                  <Box
                    key={`${row.id}-${optionIndex}`}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <IconButton
                        size="small"
                        aria-label={`Move option up`}
                        disabled={optionIndex === 0}
                        onClick={() =>
                          onChange(
                            patchRow(rows, index, {
                              options: moveOption(options, optionIndex, -1),
                            }),
                          )
                        }
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Move option down`}
                        disabled={optionIndex === options.length - 1}
                        onClick={() =>
                          onChange(
                            patchRow(rows, index, {
                              options: moveOption(options, optionIndex, 1),
                            }),
                          )
                        }
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      size="small"
                      value={option}
                      onChange={(event) => {
                        const nextOptions = options.map((text, i) =>
                          i === optionIndex ? event.target.value : text,
                        );
                        const preferredNormal =
                          row.preferredNormal === option
                            ? event.target.value
                            : row.preferredNormal;
                        onChange(
                          patchRow(rows, index, { options: nextOptions, preferredNormal }),
                        );
                      }}
                      fullWidth
                    />
                    <Tooltip title="Preferred normal">
                      <IconButton
                        size="small"
                        color={row.preferredNormal === option ? "primary" : "default"}
                        aria-label={`Set ${option} as preferred normal`}
                        onClick={() =>
                          onChange(patchRow(rows, index, { preferredNormal: option }))
                        }
                      >
                        {row.preferredNormal === option ? (
                          <Star fontSize="small" />
                        ) : (
                          <StarBorder fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      aria-label={`Remove ${option}`}
                      onClick={() => {
                        const nextOptions = options.filter((_, i) => i !== optionIndex);
                        const preferredNormal =
                          row.preferredNormal === option
                            ? nextOptions[0] || ""
                            : row.preferredNormal;
                        onChange(
                          patchRow(rows, index, { options: nextOptions, preferredNormal }),
                        );
                      }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    size="small"
                    label="New option"
                    value={draftOption}
                    onChange={(event) => setDraftOption(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      const text = draftOption.trim();
                      if (!text) return;
                      onChange(
                        patchRow(rows, index, { options: [...options, text] }),
                      );
                      setDraftOption("");
                    }}
                    fullWidth
                  />
                  <Button
                    size="small"
                    startIcon={<Add />}
                    disabled={!draftOption.trim()}
                    onClick={() => {
                      const text = draftOption.trim();
                      if (!text) return;
                      onChange(
                        patchRow(rows, index, { options: [...options, text] }),
                      );
                      setDraftOption("");
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </Paper>
        );
      })}
    </Stack>
  );
};

/** Document-mode table: findings only, no reorder chrome. */
const DocumentTable = ({ rows, onChangeFinding }) => (
  <Stack spacing={1.5}>
    {rows
      .filter((row) => !row.hidden)
      .map((row) => (
        <Box
          key={row.id}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
            gap: 1.5,
            alignItems: { sm: "center" },
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          <TextField
            size="small"
            placeholder="Findings…"
            value={row.finding}
            onChange={(event) => onChangeFinding(row.id, event.target.value)}
            fullWidth
          />
        </Box>
      ))}
  </Stack>
);

const MockExamShell = ({ children, toolbar }) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ sm: "center" }}
      justifyContent="space-between"
      sx={{ mb: 2 }}
    >
      <Typography variant="h6" component="h3">
        Exam
      </Typography>
      {toolbar}
    </Stack>
    {children}
  </Paper>
);

const Option1Mock = () => {
  const [rows, setRows] = useState(cloneSystems);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");

  return (
    <Stack spacing={2}>
      <ScopeChip />
      <FeedbackBanner message={message} onClose={() => setMessage("")} />
      <MockExamShell
        toolbar={
          editing ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                onClick={() => {
                  setEditing(false);
                  setMessage("Saved section order and labels for this note (mock).");
                }}
              >
                Save for this note
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setEditing(false);
                  setMessage(
                    "Saved as my defaults for this specialty × setting × note type (mock).",
                  );
                }}
              >
                Save as my defaults
              </Button>
              <Button size="small" color="inherit" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Button size="small" onClick={() => setEditing(true)}>
              Edit layout
            </Button>
          )
        }
      >
        {editing ? (
          <LayoutEditorList rows={rows} onChange={setRows} title="Edit layout (this note)" />
        ) : (
          <DocumentTable
            rows={rows}
            onChangeFinding={(id, finding) =>
              setRows((prev) => prev.map((row) => (row.id === id ? { ...row, finding } : row)))
            }
          />
        )}
      </MockExamShell>
    </Stack>
  );
};

const Option2Mock = () => {
  const [noteRows, setNoteRows] = useState(cloneSystems);
  const [defaultRows, setDefaultRows] = useState(cloneSystems);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [message, setMessage] = useState("");

  const activeRows = tab === 0 ? noteRows : defaultRows;
  const setActiveRows = tab === 0 ? setNoteRows : setDefaultRows;

  return (
    <Stack spacing={2}>
      <ScopeChip />
      <FeedbackBanner message={message} onClose={() => setMessage("")} />
      <MockExamShell
        toolbar={
          <Button size="small" onClick={() => setOpen(true)}>
            Customize exam…
          </Button>
        }
      >
        <DocumentTable
          rows={noteRows}
          onChangeFinding={(id, finding) =>
            setNoteRows((prev) => prev.map((row) => (row.id === id ? { ...row, finding } : row)))
          }
        />
      </MockExamShell>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Customize exam</DialogTitle>
        <DialogContent dividers>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
            <Tab label="This note" />
            <Tab label="My defaults" />
          </Tabs>
          {tab === 1 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Changes here become your personal template for {MOCK_SCOPE}, including each
              section&apos;s finding options.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reorder or hide systems for this encounter, or expand a section to edit its
              options.
            </Typography>
          )}
          <LayoutEditorListWithOptions rows={activeRows} onChange={setActiveRows} />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setOpen(false);
              setMessage(
                tab === 0
                  ? "Saved layout and section options for this note (mock)."
                  : "Saved my exam defaults, including section options (mock).",
              );
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const Option3Mock = () => {
  const [mode, setMode] = useState("document");
  const [noteRows, setNoteRows] = useState(cloneSystems);
  const [defaultRows, setDefaultRows] = useState(cloneSystems);
  const [configTarget, setConfigTarget] = useState("note");
  const [message, setMessage] = useState("");

  const configRows = configTarget === "note" ? noteRows : defaultRows;
  const setConfigRows = configTarget === "note" ? setNoteRows : setDefaultRows;

  return (
    <Stack spacing={2}>
      <ScopeChip />
      <FeedbackBanner message={message} onClose={() => setMessage("")} />
      <MockExamShell
        toolbar={
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, value) => {
              if (value) setMode(value);
            }}
          >
            <ToggleButton value="document">Document</ToggleButton>
            <ToggleButton value="configure">Configure</ToggleButton>
          </ToggleButtonGroup>
        }
      >
        {mode === "document" ? (
          <DocumentTable
            rows={noteRows}
            onChangeFinding={(id, finding) =>
              setNoteRows((prev) =>
                prev.map((row) => (row.id === id ? { ...row, finding } : row)),
              )
            }
          />
        ) : (
          <Stack spacing={2}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={configTarget}
              onChange={(_, value) => {
                if (value) setConfigTarget(value);
              }}
            >
              <ToggleButton value="note">This note</ToggleButton>
              <ToggleButton value="defaults">My defaults</ToggleButton>
            </ToggleButtonGroup>
            <LayoutEditorList rows={configRows} onChange={setConfigRows} />
            <Box>
              <Button
                size="small"
                onClick={() =>
                  setMessage(
                    configTarget === "note"
                      ? "Saved configuration for this note (mock)."
                      : "Saved my defaults (mock).",
                  )
                }
              >
                Save
              </Button>
            </Box>
          </Stack>
        )}
      </MockExamShell>
    </Stack>
  );
};

const Option4Mock = () => {
  const [noteRows, setNoteRows] = useState(cloneSystems);
  const [defaultRows, setDefaultRows] = useState(cloneSystems);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  return (
    <Stack spacing={2}>
      <ScopeChip />
      <FeedbackBanner message={message} onClose={() => setMessage("")} />
      <MockExamShell
        toolbar={
          <Button size="small" onClick={() => setOpen(true)}>
            Exam preferences
          </Button>
        }
      >
        <DocumentTable
          rows={noteRows}
          onChangeFinding={(id, finding) =>
            setNoteRows((prev) => prev.map((row) => (row.id === id ? { ...row, finding } : row)))
          }
        />
      </MockExamShell>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 400 }, p: 2.5 }} role="presentation">
          <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
            Exam preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit your defaults for {MOCK_SCOPE}, then apply them to this note if you want.
          </Typography>
          <LayoutEditorList
            rows={defaultRows}
            onChange={setDefaultRows}
            title="My defaults"
          />
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Button
              onClick={() => {
                setMessage("Saved my exam defaults (mock).");
                setOpen(false);
              }}
            >
              Save defaults
            </Button>
            <Button
              onClick={() => {
                setNoteRows(defaultRows.map((row) => ({ ...row })));
                setMessage("Applied my defaults to this note (mock).");
                setOpen(false);
              }}
            >
              Apply to this note
            </Button>
            <Button color="inherit" onClick={() => setOpen(false)}>
              Close
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Stack>
  );
};

const Option5Mock = () => {
  const [view, setView] = useState("exam");
  const [noteRows, setNoteRows] = useState(cloneSystems);
  const [defaultRows, setDefaultRows] = useState(cloneSystems);
  const [customizing, setCustomizing] = useState(false);
  const [message, setMessage] = useState("");

  if (view === "settings") {
    return (
      <Stack spacing={2}>
        <ScopeChip />
        <FeedbackBanner message={message} onClose={() => setMessage("")} />
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" component="h3">
              My Exam preferences
            </Typography>
            <Button size="small" color="inherit" onClick={() => setView("exam")}>
              Back to Exam
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Template for future notes: {MOCK_SCOPE}.
          </Typography>
          <LayoutEditorList rows={defaultRows} onChange={setDefaultRows} title="Default systems" />
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setMessage("Saved My Exam preferences (mock).")}
            >
              Save defaults
            </Button>
          </Box>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <ScopeChip />
      <FeedbackBanner message={message} onClose={() => setMessage("")} />
      <Alert severity="info">
        Defaults are edited on a separate preferences page. Open it from the toolbar, then return
        here to document.
      </Alert>
      <MockExamShell
        toolbar={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" onClick={() => setView("settings")}>
              My Exam preferences
            </Button>
            {customizing ? (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    setCustomizing(false);
                    setMessage("Saved encounter override for this note (mock).");
                  }}
                >
                  Save for this note
                </Button>
                <Button size="small" color="inherit" onClick={() => setCustomizing(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button size="small" onClick={() => setCustomizing(true)}>
                Customize for this note
              </Button>
            )}
          </Stack>
        }
      >
        {customizing ? (
          <LayoutEditorList
            rows={noteRows}
            onChange={setNoteRows}
            title="Encounter override"
          />
        ) : (
          <DocumentTable
            rows={noteRows}
            onChangeFinding={(id, finding) =>
              setNoteRows((prev) =>
                prev.map((row) => (row.id === id ? { ...row, finding } : row)),
              )
            }
          />
        )}
      </MockExamShell>
    </Stack>
  );
};

const OPTION_MOCKS = {
  1: Option1Mock,
  2: Option2Mock,
  3: Option3Mock,
  4: Option4Mock,
  5: Option5Mock,
};

const ExamEditDraftPage = () => {
  const [selected, setSelected] = useState(2);
  const Mock = useMemo(() => OPTION_MOCKS[selected] || Option1Mock, [selected]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        color: "text.primary",
        py: { xs: 3, md: 5 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Logo />
          <Typography variant="h4" component="h1">
            Exam edit / reorder — draft options
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Interactive mockups only. Choose a numbered option below, try the controls, then reply
            in chat with the option number you want rolled out across Admission H&amp;P, Progress
            Note, and Events for every specialty and setting.
          </Typography>
        </Stack>

        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {OPTIONS.map((option) => (
            <SelectableChoiceCard
              key={option.id}
              selected={selected === option.id}
              onClick={() => setSelected(option.id)}
              title={option.title}
              description={option.description}
            />
          ))}
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Preview: Option {selected}
        </Typography>
        <Mock />
      </Box>
    </Box>
  );
};

export default ExamEditDraftPage;
